<script lang="ts">
import { FunctionalComponent, h } from 'vue'
import { CLICKABLE_TEXT_CLASS } from '../utils/const'

type Props = {
  text: string | number
  /** Raw numeric value + its field, for `usePivotSelection`'s sum. */
  rawValue?: unknown
  field: string
  parentPath?: string
  /** Whether the cell is in the currently-revealed part of the tree (Modern layout only). */
  dataCell?: boolean
  hidden?: boolean
  total?: boolean
  /** Body row index (0-based) — see `usePivotSelection`. Gated by `dataCell` too, like `data-cell`
   * itself: a collapsed row's cells stay mounted but shouldn't be selectable. */
  row?: number
  /** 0-based leaf-column position, left to right. */
  col?: number
  /** This field is in `SIGN_COLORED_FIELDS_PROVIDE_KEY` — color a nonzero `rawValue` red/green by sign. */
  signColored?: boolean
  /** Whether the cell is clickable. */
  isClickableValue?: boolean
}
/**
 * Functional component, not stateful `<script setup>` — this is the grid's highest-cardinality
 * leaf (one per row × value field, up to `PIVOT_TABLE_LAZY_COUNT`/`MAX_EXPAND_ALL_TOTAL_ROWS`).
 * At 100k cells: ~400ms median mount for a stateful version vs ~255ms functional (~165ms for
 * plain vnodes). `PivotHeaderCell`/`PivotFooterCell` stay ordinary SFCs — columns-only cardinality.
 *
 * Still `.vue`, not `.ts` — Tailwind's `content` globs only scan `.vue`/`.html`, so a `.ts`
 * file's class strings would ship with no generated CSS.
 *
 * `content-visibility: auto` + `contain-intrinsic-height` let the browser skip layout/paint for
 * off-screen cells instead of unmounting them. Selection no longer rules out real virtualization —
 * `usePivotSelection` addresses cells by their own `data-row`/`data-col`, not DOM order — but this
 * still cuts the render cost that dominates at this cardinality. The height is a guess (matches the
 * lazy-load skeleton's `h-8`); `auto` means the browser remembers the real size after first layout,
 * so the guess only matters before a cell has ever been laid out.
 */
// Inline literal Tailwind classes, not `cn()` — this is the one place in the grid where cn()
// would run tens of thousands of times. `total`/`hidden` are purely additive, so no merge needed.
// Text color is appended separately (see `textColorClass`), never baked in here, so the two never
// have to be merged for a conflict.
const BASE_CLASS = 'py-3.5 px-4 relative flex items-center justify-end font-ds text-sm sm:text-xs text-right overflow-hidden number-node [content-visibility:auto] [contain-intrinsic-height:auto_32px] cursor-cell data-row-hovered:bg-[#fafaff] data-row-hovered:dark:bg-[#151520]'

// `-subtle-foreground`, not the bare `success`/`destructive` fill — the fill tokens are below
// 4.5:1 on a light surface (see `ds-tokens.css`'s own "NOTE ON GRAPHICAL USE"), so only the
// `-subtle-foreground` step is AA-safe as body/number text. Zero stays neutral: a flat number
// isn't "up" or "down".
const textColorClass = (signColored: boolean | undefined, rawValue: unknown): string => {
  if (!signColored || typeof rawValue !== 'number' || rawValue === 0) return 'text-foreground'
  return rawValue < 0 ? 'text-destructive-subtle-foreground' : 'text-success-subtle-foreground'
}

const pivotValueCellText = (props: Props, emit: (event: 'click', ...args: any[]) => void) => {
  const clickable = props.isClickableValue && typeof props.rawValue === 'number'
  // Native button, not a clicked div — keyboard and screen-reader users need to reach this the
  // same way mouse users do. Preflight resets a `button`'s margin/padding/border/font/color to
  // match surrounding text, so only the focus ring is added on top of `CLICKABLE_TEXT_CLASS`.
  return h(clickable ? 'button' : 'div', {
    type: clickable ? 'button' : undefined,
    class: `truncate${clickable ? ` ${CLICKABLE_TEXT_CLASS} focus-visible:outline-none focus-visible:shadow-ds-focus rounded-ds-sm` : ''}`,
    onClick: clickable ? () => emit('click', props.rawValue as number) : undefined,
  }, props.text)
}

const PivotValueCell: FunctionalComponent<Props, ['click']> = (props, { emit }) => h('div', {
  'data-parent-path': props.parentPath,
  'data-cell': props.dataCell || undefined,
  'data-row': props.dataCell ? props.row : undefined,
  'data-col': props.dataCell ? props.col : undefined,
  'data-raw-value': typeof props.rawValue === 'number' ? props.rawValue : undefined,
  'data-value-field': props.field,
  class: `${BASE_CLASS} ${textColorClass(props.signColored, props.rawValue)}${props.total ? ' bg-[#fafaff] dark:bg-[#151520] font-bold' : ' bg-card'}`,
  // Inline style, not a class: always wins over the base's `flex`, cheaper than cn().
  style: props.hidden ? { display: 'none' } : undefined,
}, pivotValueCellText(props, emit))

PivotValueCell.props = ['text', 'rawValue', 'field', 'parentPath', 'dataCell', 'hidden', 'total', 'row', 'col', 'signColored', 'isClickableValue']
PivotValueCell.emits = ['click']

export default PivotValueCell
</script>
